import type { ScreenBase } from "@frui.ts/screens";
import { Router, watchBusy } from "@frui.ts/screens";
import type { PropertyName } from "@frui.ts/helpers";
import { bound } from "@frui.ts/helpers";
import * as Sentry from "@sentry/react";
import { computed } from "mobx";
import { Modules } from "config";
import CustomerRefundCandidateResponse from "entities/customerRefundCandidateResponse";
import CustomerRefundCandidateSearchRequest from "entities/customerRefundCandidateSearchRequest";
import type RefundPaymentType from "entities/refundPaymentType";
import ConfirmationService from "services/confirmationService";
import SecurityService from "services/securityService";
import EventBus, {
  approvedChargeRefundCandidatesCreated,
  chargePartialRefundCandidateCreated,
  chargeRefundCandidateCreated,
  customerRefundCandidateExported,
} from "services/eventBus";
import EnumService from "services/enumService";
import LocalizationService from "services/localizationService";
import NotificationService, { NotificationType } from "services/notificationService";
import CustomerRefundCandidatesRepository from "repositories/customerRefundCandidatesRepository";
import ListViewModel from "../../../listViewModel";
import type CountrySelectViewModel from "../../../countrySelectViewModel";
import type PagingManagerViewModel from "../../../pagingManagerViewModel";
import type CandidateRejectionViewModel from "./candidateRejectionViewModel";
import type CustomerRefundCandidateDetailViewModel from "./customerRefundCandidateDetailViewModel";
import Selectable, { SelectableListViewModel } from "../../../selectableListViewModel";
import ItemApprovalMethod from "entities/itemApprovalMethod";

const navigationName = "customer-refund-candidates";

@Router.registerRoute({ route: navigationName, name: Router.Self })
@Selectable
export default class CustomerRefundCandidatesViewModel extends ListViewModel<
  CustomerRefundCandidateResponse,
  CustomerRefundCandidateDetailViewModel,
  ScreenBase,
  CustomerRefundCandidateSearchRequest
> {
  countrySelectViewModel: CountrySelectViewModel;
  readonly modelName = "customerRefundCandidate";

  @computed
  get name() {
    return this.translate("name");
  }

  @computed
  get self(): SelectableListViewModel<CustomerRefundCandidatesViewModel, CustomerRefundCandidateResponse> {
    return this as any;
  }

  @computed
  get allowDelete() {
    return false;
  }

  @computed
  get isUserAllowedToEdit() {
    return this.securityService.allowTo("finance.factualauditing", "write");
  }

  constructor(
    protected localizationService: LocalizationService,
    protected securityService: SecurityService,
    protected notificationService: NotificationService,
    protected confirmationService: ConfirmationService,
    protected eventBus: EventBus,
    protected repository: CustomerRefundCandidatesRepository,
    protected detailViewModelFactory: ReturnType<typeof CustomerRefundCandidateDetailViewModel.Factory>,
    private candidateRejectionViewModelFactory: ReturnType<typeof CandidateRejectionViewModel.Factory>,
    private enumService: EnumService,
    private countrySelectViewModelFactory: ReturnType<typeof CountrySelectViewModel.Factory>,
    private pagingManagerFactory: ReturnType<typeof PagingManagerViewModel.Factory>
  ) {
    super();
    this.navigationName = navigationName;
    this.countrySelectViewModel = this.countrySelectViewModelFactory("id");
  }

  protected processData(items: CustomerRefundCandidateResponse[]) {
    items.forEach(item => {
      item.isSelectable = item.permittedActions.refundation.isAllowed;

      return item;
    });

    return items;
  }

  onInitialize(): Promise<any> | void {
    this.unsubscriptions.push(
      this.eventBus.subscribe(chargeRefundCandidateCreated, this.loadData),
      this.eventBus.subscribe(chargePartialRefundCandidateCreated, this.loadData),
      this.eventBus.subscribe(approvedChargeRefundCandidatesCreated, this.loadData)
    );
    return super.onInitialize();
  }

  @bound
  allowItemDetail(item: CustomerRefundCandidateResponse) {
    return false;
  }

  protected createFilter(): CustomerRefundCandidateSearchRequest {
    return new CustomerRefundCandidateSearchRequest();
  }

  protected detailParams(itemId: any): any[] {
    const pagingManager = this.pagingManagerFactory(this, itemId);
    return [pagingManager];
  }

  @bound
  async onApproveSelectedClick() {
    const confirmed = await this.confirmationService.showConfirmation(this.translate("confirmSelectedApproval.title"), {
      confirmButton: { text: this.translate("confirmSelectedApproval.confirm"), variant: "danger" },
    });

    if (confirmed) {
      try {
        await this.repository.generateAll({
          approvalMethod: ItemApprovalMethod.ApproveSelected,
          customerRefundCandidateIds: Array.from(this.self.selectedItems).map(item => item.id),
        });
        this.eventBus.publish(approvedChargeRefundCandidatesCreated());
        this.notificationService.add({ title: this.translate("selectedGenerated"), type: NotificationType.SUCCESS });
        this.self.selectedItems.clear();
      } catch (e) {
        // General error handle is good enough
      }
    }
  }

  @bound
  @watchBusy
  async onGenerateRefundClick(item: CustomerRefundCandidateResponse) {
    if (!item.permittedActions.refundation.isAllowed) {
      return;
    }

    try {
      const confirmed = await this.confirmationService.showConfirmation(this.translate("generateRefundConfirmation.message"), {
        title: this.translate("generateRefundConfirmation.title"),
        confirmButton: { variant: "primary", text: this.translate("generateRefundConfirmation.confirm") },
      });

      if (confirmed) {
        await this.repository.generateRefund(item.id);
        this.eventBus.publish(customerRefundCandidateExported({ customerRefundCandidate: item }));
        this.notificationService.add({ title: this.translate("refundGenerated"), type: NotificationType.SUCCESS });
        this.self.selectedItems.clear();
      }
    } catch (e) {
      await this.loadData();
    }
  }

  @bound
  onRejectionClick(item: CustomerRefundCandidateResponse) {
    this.tryActivateChild(this.candidateRejectionViewModelFactory(item));
  }

  @bound
  async generateAll() {
    const confirmed = await this.confirmationService.showConfirmation(this.translate("confirmGenerateAll.title"), {
      confirmButton: { text: this.translate("confirmGenerateAll.confirm"), variant: "danger" },
    });

    if (!confirmed) {
      return;
    }

    try {
      await this.repository.generateAll({ approvalMethod: ItemApprovalMethod.ApproveAll });
      this.notificationService.add({ title: this.translate("allGenerated"), type: NotificationType.SUCCESS });
      this.self.selectedItems.clear();
      await this.loadData();
    } catch (error) {
      Sentry.captureException(error);
    }
  }

  refundPaymentType(refundPaymentType: RefundPaymentType) {
    return this.localizationService.translateEnum("refundPaymentType", refundPaymentType);
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.finance, `customerRefundCandidates.${code}`, size);
  }

  translateFilterAttribute(attribute: PropertyName<CustomerRefundCandidateSearchRequest>, size?: number) {
    return this.localizationService.translateAttribute("customerRefundCandidateSearchRequest", attribute, size);
  }
}
